export type JsonInsertCallback = (json: string) => void;

export class JsonPanel {
  private container: HTMLElement;
  private isVisible: boolean = false;
  private inputArea: HTMLTextAreaElement | null = null;
  private outputArea: HTMLElement | null = null;
  private errorArea: HTMLElement | null = null;
  private onInsert: JsonInsertCallback | null = null;

  constructor(containerId: string) {
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container ${containerId} not found`);
    }
    this.container = container;
    this.render();
    this.setupEventListeners();
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="json-panel-header">
        <span class="json-panel-title">JSON Tools</span>
      </div>
      <div class="json-panel-content">
        <textarea
          class="json-input"
          placeholder="Pega tu JSON aqui..."
          spellcheck="false"
        ></textarea>
        <div class="json-toolbar">
          <button class="json-btn" data-action="beautify" title="Formatear JSON">Beautify</button>
          <button class="json-btn" data-action="minify" title="Comprimir JSON">Minify</button>
          <button class="json-btn" data-action="validate" title="Validar JSON">Validate</button>
          <button class="json-btn" data-action="copy" title="Copiar al portapapeles">Copy</button>
          <button class="json-btn json-btn-accent" data-action="insert" title="Insertar en editor">Insert</button>
        </div>
        <div class="json-error hidden"></div>
        <div class="json-output-container hidden">
          <div class="json-output-header">
            <span>Result:</span>
            <button class="json-path-toggle" data-action="toggle-path" title="Mostrar JSON Path">Path</button>
          </div>
          <pre class="json-output"></pre>
        </div>
        <div class="json-path-viewer hidden">
          <div class="json-path-header">JSON Path Navigator</div>
          <div class="json-path-tree"></div>
        </div>
      </div>
    `;

    this.inputArea = this.container.querySelector('.json-input');
    this.outputArea = this.container.querySelector('.json-output');
    this.errorArea = this.container.querySelector('.json-error');
  }

  private setupEventListeners(): void {
    this.container.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const action = target.getAttribute('data-action');

      if (!action) return;

      switch (action) {
        case 'beautify':
          this.beautify();
          break;
        case 'minify':
          this.minify();
          break;
        case 'validate':
          this.validate();
          break;
        case 'copy':
          this.copyToClipboard();
          break;
        case 'insert':
          this.insertIntoEditor();
          break;
        case 'toggle-path':
          this.togglePathViewer();
          break;
      }
    });
  }

  private getInput(): string {
    return this.inputArea?.value.trim() || '';
  }

  private setOutput(text: string): void {
    if (!this.outputArea) return;

    const container = this.outputArea.closest('.json-output-container');
    container?.classList.remove('hidden');
    this.outputArea.textContent = text;
  }

  private showError(message: string): void {
    if (!this.errorArea) return;

    this.errorArea.classList.remove('hidden');
    this.errorArea.textContent = message;

    // Hide output when showing error
    const outputContainer = this.container.querySelector('.json-output-container');
    outputContainer?.classList.add('hidden');
  }

  private clearError(): void {
    this.errorArea?.classList.add('hidden');
  }

  private parseJson(input: string): { success: boolean; data?: unknown; error?: string } {
    try {
      const data = JSON.parse(input);
      return { success: true, data };
    } catch (e) {
      const error = e as SyntaxError;
      return { success: false, error: this.formatParseError(error, input) };
    }
  }

  private formatParseError(error: SyntaxError, input: string): string {
    // Extract position from error message if available
    const match = error.message.match(/position\s+(\d+)/i);
    if (match) {
      const pos = parseInt(match[1], 10);
      const lines = input.substring(0, pos).split('\n');
      const line = lines.length;
      const column = lines[lines.length - 1].length + 1;
      return `Error en linea ${line}, columna ${column}: ${error.message}`;
    }
    return error.message;
  }

  beautify(): void {
    this.clearError();
    const input = this.getInput();

    if (!input) {
      this.showError('No hay JSON para formatear');
      return;
    }

    const result = this.parseJson(input);

    if (result.success) {
      const formatted = JSON.stringify(result.data, null, 2);
      this.setOutput(formatted);
      // Also update input area for subsequent operations
      if (this.inputArea) {
        this.inputArea.value = formatted;
      }
    } else {
      this.showError(result.error || 'JSON invalido');
    }
  }

  minify(): void {
    this.clearError();
    const input = this.getInput();

    if (!input) {
      this.showError('No hay JSON para comprimir');
      return;
    }

    const result = this.parseJson(input);

    if (result.success) {
      const minified = JSON.stringify(result.data);
      this.setOutput(minified);
      if (this.inputArea) {
        this.inputArea.value = minified;
      }
    } else {
      this.showError(result.error || 'JSON invalido');
    }
  }

  validate(): void {
    this.clearError();
    const input = this.getInput();

    if (!input) {
      this.showError('No hay JSON para validar');
      return;
    }

    const result = this.parseJson(input);

    if (result.success) {
      // Show success with stats
      const stats = this.getJsonStats(result.data);
      this.setOutput(`JSON valido!\n\n${stats}`);
    } else {
      this.showError(result.error || 'JSON invalido');
    }
  }

  private getJsonStats(data: unknown): string {
    const type = Array.isArray(data) ? 'Array' : typeof data;
    let stats = `Tipo: ${type}`;

    if (Array.isArray(data)) {
      stats += `\nElementos: ${data.length}`;
    } else if (typeof data === 'object' && data !== null) {
      stats += `\nPropiedades: ${Object.keys(data).length}`;
    }

    const size = new Blob([JSON.stringify(data)]).size;
    stats += `\nTamano: ${this.formatBytes(size)}`;

    return stats;
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  async copyToClipboard(): Promise<void> {
    const output = this.outputArea?.textContent || this.getInput();

    if (!output) {
      this.showError('No hay contenido para copiar');
      return;
    }

    try {
      await navigator.clipboard.writeText(output);
      // Visual feedback
      const copyBtn = this.container.querySelector('[data-action="copy"]');
      if (copyBtn) {
        const originalText = copyBtn.textContent;
        copyBtn.textContent = 'Copiado!';
        setTimeout(() => {
          copyBtn.textContent = originalText;
        }, 1500);
      }
    } catch (e) {
      this.showError('Error al copiar al portapapeles');
    }
  }

  insertIntoEditor(): void {
    const output = this.outputArea?.textContent || this.getInput();

    if (!output) {
      this.showError('No hay contenido para insertar');
      return;
    }

    if (this.onInsert) {
      this.onInsert(output);
    }
  }

  private togglePathViewer(): void {
    const pathViewer = this.container.querySelector('.json-path-viewer');
    const toggleBtn = this.container.querySelector('[data-action="toggle-path"]');

    if (pathViewer?.classList.contains('hidden')) {
      this.renderPathViewer();
      pathViewer?.classList.remove('hidden');
      toggleBtn?.classList.add('active');
    } else {
      pathViewer?.classList.add('hidden');
      toggleBtn?.classList.remove('active');
    }
  }

  private renderPathViewer(): void {
    const input = this.getInput();
    const tree = this.container.querySelector('.json-path-tree');

    if (!tree || !input) return;

    const result = this.parseJson(input);
    if (!result.success) {
      tree.innerHTML = '<div class="json-path-error">JSON invalido</div>';
      return;
    }

    tree.innerHTML = this.buildPathTree(result.data, '$');
  }

  private buildPathTree(data: unknown, path: string, depth: number = 0): string {
    if (depth > 5) return ''; // Limit depth to prevent performance issues

    const type = Array.isArray(data) ? 'array' : typeof data;
    let html = '';

    if (type === 'object' && data !== null) {
      for (const key of Object.keys(data as Record<string, unknown>)) {
        const childPath = `${path}.${key}`;
        const childValue = (data as Record<string, unknown>)[key];
        const childType = Array.isArray(childValue) ? 'array' : typeof childValue;
        const isExpandable = childType === 'object' || childType === 'array';

        html += `
          <div class="json-path-item" style="padding-left: ${depth * 16}px" data-path="${childPath}">
            <span class="json-path-key">${key}</span>
            <span class="json-path-type">${childType}</span>
          </div>
        `;

        if (isExpandable && childValue !== null) {
          html += this.buildPathTree(childValue, childPath, depth + 1);
        }
      }
    } else if (type === 'array') {
      const arr = data as unknown[];
      arr.slice(0, 10).forEach((item: unknown, index: number) => {
        const childPath = `${path}[${index}]`;
        const childType = Array.isArray(item) ? 'array' : typeof item;
        const isExpandable = childType === 'object' || childType === 'array';

        html += `
          <div class="json-path-item" style="padding-left: ${depth * 16}px" data-path="${childPath}">
            <span class="json-path-index">[${index}]</span>
            <span class="json-path-type">${childType}</span>
          </div>
        `;

        if (isExpandable && item !== null) {
          html += this.buildPathTree(item, childPath, depth + 1);
        }
      });

      if (arr.length > 10) {
        html += `<div class="json-path-more" style="padding-left: ${depth * 16}px">... y ${arr.length - 10} mas</div>`;
      }
    }

    return html;
  }

  // Public API methods (matching existing panel pattern)
  show(): void {
    this.isVisible = true;
    this.container.classList.remove('hidden');
  }

  hide(): void {
    this.isVisible = false;
    this.container.classList.add('hidden');
  }

  toggle(): boolean {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
    return this.isVisible;
  }

  isShowing(): boolean {
    return this.isVisible;
  }

  // Callback for editor insertion
  onInsertToEditor(callback: JsonInsertCallback): void {
    this.onInsert = callback;
  }

  // Clear panel state
  clear(): void {
    if (this.inputArea) {
      this.inputArea.value = '';
    }
    this.clearError();
    this.container.querySelector('.json-output-container')?.classList.add('hidden');
    this.container.querySelector('.json-path-viewer')?.classList.add('hidden');
  }
}
